import {
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  IconButton,
  InputBase,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {styled} from "@mui/material/styles";
import {
  Add as AddIcon,
  Search as SearchIcon,
  EditOutlined as EditIcon,
  DeleteOutline as DeleteIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  People as PeopleIcon,
  Person as PersonIcon,
  AccessTime as AccessTimeIcon,
  Business as BusinessIcon,
  Public as PublicIcon,
  NotificationsNone as NotifIcon,
} from "@mui/icons-material";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useNavigate, useLocation} from "react-router-dom";
import {
  getScheduledNotifications,
  deleteScheduledNotification,
  toggleScheduledNotificationStatus,
  DAYS_OF_WEEK,
  NOTIFICATION_SCOPES,
  SCOPE_LABELS,
} from "../services/ScheduledNotificationService";
import {getCompanies} from "../services/system/CompanyService";
import {getTrainers} from "../services/TrainerService";
import {useAuth} from "../contexts/AuthContext";
import {useCompany} from "../contexts/CompanyContext";
import ScheduledNotificationDialog from "../componenets/notifications/ScheduledNotificationDialog";
import {RTL} from "../cache/CacheRtl";
import {Pagination} from "../componenets/ui";
import {borders, shadows} from "../componenets/ui/colors";
import {t} from "../componenets/dashboard/coach/coachTokens";

/* ── Styled FilterSelect (coach-token accent) ────────────── */
const FilterSelect = styled(Autocomplete)({
  minWidth: "160px",
  "& .MuiOutlinedInput-root": {
    color: t.t2, borderRadius: t.rs,
    backgroundColor: t.white, fontSize: "14px", fontWeight: 500, height: "40px",
    "& .MuiOutlinedInput-notchedOutline": {borderColor: t.border},
    "&:hover .MuiOutlinedInput-notchedOutline": {borderColor: t.green},
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {borderColor: t.green, borderWidth: "2px"},
  },
  "& .MuiInputLabel-root": {color: t.t2, fontSize: "14px", "&.Mui-focused": {color: t.green}},
});

/* ── Constants ───────────────────────────────────────────── */
const PAGE_SIZE = 20;

const STATUS_FILTERS = [
  {value: null, label: "הכל"},
  {value: true, label: "פעיל"},
  {value: false, label: "לא פעיל"},
];

/* ── Helper functions ────────────────────────────────────── */
const getDaysDisplay = (scheduleDays) => {
  if (!scheduleDays || scheduleDays.length === 0) return "-";
  if (scheduleDays.length === 7) return "כל יום";
  const dayOrder = DAYS_OF_WEEK.map(d => d.value);
  const sorted = [...scheduleDays].sort((a, b) => dayOrder.indexOf(a) - dayOrder.indexOf(b));
  return sorted.map(day => {
    const info = DAYS_OF_WEEK.find(d => d.value === day);
    return info?.shortLabel || day.charAt(0);
  }).join(", ");
};

/* ── Table cell presets (match Clients/CoachDashboard) ────── */
const td = {padding: "10px 12px", borderBottom: `1px solid ${t.borderLight}`, fontSize: "12.5px", color: t.text, verticalAlign: "middle"};
const th = {padding: "10px 12px", fontSize: "10.5px", fontWeight: 700, color: t.t3, borderBottom: `1px solid ${t.border}`,
            background: t.bg, whiteSpace: "nowrap", textTransform: "uppercase", letterSpacing: ".06em"};

/* ═══════════════════════════════════════════════════════════
   Notifications page
   ═══════════════════════════════════════════════════════════ */
const NotificationsPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const {isSuperAdmin, isCompanyAdmin, isTrainer} = useAuth();
  const {company} = useCompany();
  const query = useMemo(() => new URLSearchParams(location.search), [location.search]);

  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeNotification, setActiveNotification] = useState(null);

  // Filter dropdown data
  const [companies, setCompanies] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [selectedTrainer, setSelectedTrainer] = useState(null);

  const [f, setF] = useState({
    notifications: [],
    total: 0,
    keyword: query.get("keyword") || "",
    companyId: null,
    trainerId: null,
    isActive: query.get("isActive") === "true" ? true : query.get("isActive") === "false" ? false : null,
    page: +(query.get("page") || 0),
    loading: true,
  });

  // URL sync
  const sync = useCallback((ns) => {
    const p = new URLSearchParams();
    if (ns.keyword) p.set("keyword", ns.keyword);
    if (ns.page > 0) p.set("page", ns.page);
    if (ns.isActive !== null) p.set("isActive", ns.isActive);
    navigate(p.toString() ? `${location.pathname}?${p}` : location.pathname, {replace: true});
  }, [navigate, location.pathname]);

  const set = useCallback((patch) => { const ns = {...f, page: 0, ...patch}; setF(ns); sync(ns); }, [f, sync]);

  // Debounced search
  const [searchInput, setSearchInput] = useState(f.keyword);
  const debounceRef = useRef(null);
  const handleSearch = useCallback((val) => {
    setSearchInput(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => set({keyword: val}), 300);
  }, [set]);

  // Load companies (super admin only)
  useEffect(() => {
    if (isSuperAdmin()) {
      getCompanies().then(res => setCompanies(res.data || [])).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load trainers when company changes
  useEffect(() => {
    const effectiveCompanyId = isSuperAdmin() ? f.companyId : company?.id;
    if (effectiveCompanyId) {
      getTrainers(effectiveCompanyId).then(res => setTrainers(res.data || [])).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.companyId, company?.id]);

  // Fetch notifications
  useEffect(() => {
    let effectiveCompanyId = f.companyId;
    let effectiveTrainerId = f.trainerId;
    if (!isSuperAdmin()) effectiveCompanyId = company?.id;
    // The filter takes a TRAINER id, not a user id — a plain trainer sends none and the server
    // narrows the search to their own notifications from the principal.
    if (isTrainer() && !isCompanyAdmin() && !isSuperAdmin()) effectiveTrainerId = null;

    // Wait for company context for non-super-admins
    if (!isSuperAdmin() && !company?.id) return;

    setF(p => ({...p, loading: true}));
    getScheduledNotifications(f.page, PAGE_SIZE, f.keyword || null, effectiveCompanyId, f.isActive, effectiveTrainerId)
        .then(r => setF(p => ({
          ...p,
          notifications: r.data?.content || [],
          total: r.data?.totalElements || r.data?.numberOfElements || 0,
          loading: false,
        })))
        .catch(() => setF(p => ({...p, loading: false})));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.page, f.keyword, f.companyId, f.trainerId, f.isActive, company?.id]);

  const reload = () => {
    let effectiveCompanyId = f.companyId;
    let effectiveTrainerId = f.trainerId;
    if (!isSuperAdmin()) effectiveCompanyId = company?.id;
    // The filter takes a TRAINER id, not a user id — a plain trainer sends none and the server
    // narrows the search to their own notifications from the principal.
    if (isTrainer() && !isCompanyAdmin() && !isSuperAdmin()) effectiveTrainerId = null;

    setF(p => ({...p, loading: true}));
    getScheduledNotifications(f.page, PAGE_SIZE, f.keyword || null, effectiveCompanyId, f.isActive, effectiveTrainerId)
        .then(r => setF(p => ({
          ...p,
          notifications: r.data?.content || [],
          total: r.data?.totalElements || r.data?.numberOfElements || 0,
          loading: false,
        })))
        .catch(() => setF(p => ({...p, loading: false})));
  };

  const pages = Math.ceil(f.total / PAGE_SIZE);

  // Company filter handler
  const handleCompanyChange = (_, newValue) => {
    setSelectedCompany(newValue);
    setSelectedTrainer(null);
    set({companyId: newValue?.id || null, trainerId: null});
  };

  // Trainer filter handler
  const handleTrainerChange = (_, newValue) => {
    setSelectedTrainer(newValue);
    set({trainerId: newValue?.id || null});
  };

  // Dialog handlers
  const openCreate = () => { setActiveNotification(null); setDialogOpen(true); };
  const openEdit = (n) => (e) => { e?.stopPropagation(); setActiveNotification(n); setDialogOpen(true); };
  const closeDialog = () => { setDialogOpen(false); setActiveNotification(null); };

  // Row actions
  const handleToggleStatus = (n) => async (e) => {
    e.stopPropagation();
    try {
      await toggleScheduledNotificationStatus(n.id, !n.active);
      reload();
    } catch (error) {
      console.error("Error toggling notification status:", error);
    }
  };

  const handleDelete = (n) => async (e) => {
    e.stopPropagation();
    try {
      await deleteScheduledNotification(n.id);
      reload();
    } catch (error) {
      console.error("Error deleting notification:", error);
    }
  };

  /* ── Render ──────────────────────────────────────────── */
  return (
      <Box sx={{display: "flex", flexDirection: "column", gap: "18px"}}>

        {/* Dialog */}
        <ScheduledNotificationDialog
            key={activeNotification ? `edit-${activeNotification.id}` : "create"}
            isOpen={dialogOpen}
            close={closeDialog}
            setReload={reload}
            editNotification={activeNotification}
        />

        {/* ─── Title row (no white band — sits on canvas) ─── */}
        <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0}}>
          <Box>
            <Typography sx={{fontSize: {xs: "22px", md: "26px"}, fontWeight: 800, color: t.text, lineHeight: 1.2, letterSpacing: "-0.3px"}}>
              התראות מתוזמנות
            </Typography>
            <Typography sx={{fontSize: "13px", color: t.t2, mt: 0.25}}>ניהול התראות</Typography>
          </Box>
          <Box component="button" onClick={openCreate} sx={{
            display: "inline-flex", alignItems: "center", gap: "5px",
            background: t.green, color: "#fff",
            height: 34, px: "15px", borderRadius: t.rs,
            fontSize: "13px", fontWeight: 600, letterSpacing: "-0.1px",
            border: "none", cursor: "pointer", fontFamily: "inherit",
            transition: "all .14s",
            "&:hover": {background: t.greenDark, transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(1,187,167,.35)"},
          }}>
            <AddIcon sx={{fontSize: 16}}/>
            התראה חדשה
          </Box>
        </Box>

        {/* ─── Table card ──────────────────────────────── */}
        <Box sx={{
          background: t.white, border: `1px solid ${t.border}`, borderRadius: t.r,
          boxShadow: t.sh, overflow: "hidden",
          display: "flex", flexDirection: "column",
        }}>

          {/* Filter bar inside card */}
          <Box sx={{
            display: "flex", alignItems: "center", gap: "7px",
            padding: "10px 14px", borderBottom: `1px solid ${t.border}`,
            background: t.bg, flexWrap: "wrap", flexShrink: 0,
          }}>
            {/* Count pill */}
            <Box sx={{
              display: "inline-flex", alignItems: "center", gap: "4px",
              fontSize: "12px", fontWeight: 700, color: t.greenText,
              background: t.greenLight, border: "1px solid rgba(1,187,167,.2)",
              padding: "4px 12px", borderRadius: "20px", whiteSpace: "nowrap",
            }}>
              <NotifIcon sx={{fontSize: 14}}/>
              {f.total} התראות
            </Box>

            {/* Search */}
            <Box sx={{position: "relative"}}>
              <SearchIcon sx={{position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: t.t3, pointerEvents: "none"}}/>
              <InputBase
                  value={searchInput}
                  onChange={e => handleSearch(e.target.value)}
                  placeholder="חיפוש לפי כותרת..."
                  sx={{
                    width: 200,
                    padding: "6px 10px 6px 30px",
                    border: `1.5px solid ${t.border}`,
                    borderRadius: t.rs,
                    background: t.white,
                    color: t.text,
                    fontSize: "12px",
                    "& input": {padding: 0},
                    "& input::placeholder": {color: t.t3, opacity: 1},
                    "&:focus-within": {borderColor: t.green, boxShadow: "0 0 0 3px rgba(1,187,167,.08)"},
                  }}
              />
            </Box>

            {/* Status chips */}
            <Box sx={{display: "flex", gap: 1}}>
              {STATUS_FILTERS.map(sf => {
                const active = f.isActive === sf.value;
                return (
                    <Chip key={String(sf.value)} label={sf.label}
                          onClick={() => set({isActive: sf.value})}
                          sx={{
                            fontWeight: 600, fontSize: "13px", height: "32px",
                            backgroundColor: active ? t.green : t.greenLight,
                            color: active ? "#fff" : t.t2,
                            "&:hover": {backgroundColor: active ? t.greenDark : t.border},
                          }}/>
                );
              })}
            </Box>

            {/* Company filter — super admin only */}
            {isSuperAdmin() && (
                <RTL>
                  <FilterSelect size="small"
                                options={companies}
                                getOptionLabel={o => o.name || ""}
                                value={selectedCompany}
                                onChange={handleCompanyChange}
                                isOptionEqualToValue={(o, v) => o.id === v.id}
                                renderInput={p => <TextField {...p} label="חברה" size="small"/>}
                                slotProps={{popper: {sx: {zIndex: 1300}}, paper: {sx: {borderRadius: borders.radius.md, border: borders.default, boxShadow: shadows.dropdown}}}}
                  />
                </RTL>
            )}

            {/* Trainer filter — admin+ */}
            {(isSuperAdmin() || isCompanyAdmin()) && trainers.length > 0 && (
                <RTL>
                  <FilterSelect size="small"
                                options={trainers}
                                getOptionLabel={o => o.name || ""}
                                value={selectedTrainer}
                                onChange={handleTrainerChange}
                                isOptionEqualToValue={(o, v) => o.id === v.id}
                                renderInput={p => <TextField {...p} label="מאמן" size="small"/>}
                                slotProps={{popper: {sx: {zIndex: 1300}}, paper: {sx: {borderRadius: borders.radius.md, border: borders.default, boxShadow: shadows.dropdown}}}}
                  />
                </RTL>
            )}

            <Box sx={{flex: 1}}/>
          </Box>

          {/* Table area */}
          {f.loading && !f.notifications.length ? (
              <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", minHeight: 540}}>
                <CircularProgress size={32} sx={{color: t.green}}/>
              </Box>
          ) : !f.notifications.length ? (
              <Box sx={{display: "flex", justifyContent: "center", alignItems: "center", minHeight: 240, flexDirection: "column", gap: 1, padding: "48px"}}>
                <NotifIcon sx={{fontSize: "48px", color: t.border}}/>
                <Typography sx={{color: t.t3, fontSize: "15px", fontWeight: 500}}>אין התראות מתוזמנות</Typography>
                <Typography sx={{color: t.t3, fontSize: "13px"}}>לחץ על "התראה חדשה" כדי ליצור התראה</Typography>
              </Box>
          ) : (
              <>
                <TableContainer sx={{overflowY: "auto", maxHeight: 540}}>
                  <Table stickyHeader sx={{tableLayout: "fixed"}}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{...th, textAlign: "right"}}>כותרת והודעה</TableCell>
                        <TableCell sx={{...th, textAlign: "right", width: "12%", display: {xs: "none", md: "table-cell"}}}>חברה</TableCell>
                        <TableCell sx={{...th, textAlign: "right", width: "10%", display: {xs: "none", md: "table-cell"}}}>מאמן</TableCell>
                        <TableCell sx={{...th, textAlign: "right", width: "14%", display: {xs: "none", md: "table-cell"}}}>תזמון</TableCell>
                        <TableCell sx={{...th, textAlign: "center", width: "10%", display: {xs: "none", md: "table-cell"}}}>קהל יעד</TableCell>
                        <TableCell sx={{...th, textAlign: "center", width: "8%", display: {xs: "none", md: "table-cell"}}}>סטטוס</TableCell>
                        <TableCell sx={{...th, textAlign: "center", width: 120}}>פעולות</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {f.notifications.map(n => (
                          <TableRow key={n.id}
                                    sx={{cursor: "pointer", "&:hover td": {background: t.greenLight}, transition: "background .12s ease"}}
                                    onClick={openEdit(n)}>
                            {/* Title & Body */}
                            <TableCell sx={{...td, textAlign: "right"}}>
                              <Typography sx={{fontSize: "13px", fontWeight: 600, color: t.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", mb: n.body ? 0.5 : 0}}>
                                {n.title}
                              </Typography>
                              {n.body && (
                                  <Tooltip title={n.body} placement="top">
                                    <Typography sx={{fontSize: "12px", color: t.t2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "300px"}}>
                                      {n.body}
                                    </Typography>
                                  </Tooltip>
                              )}
                            </TableCell>

                            {/* Company / Scope */}
                            <TableCell sx={{...td, textAlign: "right", display: {xs: "none", md: "table-cell"}}}>
                              {n.scope === NOTIFICATION_SCOPES.GLOBAL ? (
                                  <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                                    <PublicIcon sx={{fontSize: "16px", color: t.blue}}/>
                                    <Typography sx={{fontSize: "13px", fontWeight: 500, color: t.blue}}>{SCOPE_LABELS.GLOBAL}</Typography>
                                  </Box>
                              ) : n.scope === NOTIFICATION_SCOPES.MULTI_COMPANY ? (
                                  <Tooltip title={n.targetCompanies?.map(c => c.name).join(", ") || ""} placement="top">
                                    <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                                      <BusinessIcon sx={{fontSize: "16px", color: t.purple}}/>
                                      <Typography sx={{fontSize: "13px", fontWeight: 500, color: t.purple}}>{n.targetCompanies?.length || 0} חברות</Typography>
                                    </Box>
                                  </Tooltip>
                              ) : (
                                  <Typography sx={{fontSize: "13px", fontWeight: 500, color: t.t2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                                    {n.companyName || "-"}
                                  </Typography>
                              )}
                            </TableCell>

                            {/* Trainer */}
                            <TableCell sx={{...td, textAlign: "right", display: {xs: "none", md: "table-cell"}}}>
                              <Typography sx={{fontSize: "13px", fontWeight: 500, color: t.t2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                                {n.trainerName || "-"}
                              </Typography>
                            </TableCell>

                            {/* Schedule */}
                            <TableCell sx={{...td, textAlign: "right", display: {xs: "none", md: "table-cell"}}}>
                              <Typography sx={{fontSize: "13px", fontWeight: 500, color: t.text}}>
                                {getDaysDisplay(n.scheduleDays)}
                              </Typography>
                              <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
                                <AccessTimeIcon sx={{fontSize: "14px", color: t.t3}}/>
                                <Typography sx={{fontSize: "12px", color: t.t3}}>{n.scheduleTime || "-"}</Typography>
                              </Box>
                            </TableCell>

                            {/* Audience */}
                            <TableCell sx={{...td, textAlign: "center", display: {xs: "none", md: "table-cell"}}}>
                              {n.audienceType === "SPECIFIC_USERS" ? (
                                  <Box sx={{display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5}}>
                                    <PersonIcon sx={{fontSize: "16px", color: t.t2}}/>
                                    <Typography sx={{fontSize: "13px", color: t.t2}}>{n.targetUsers?.length || 0} נבחרים</Typography>
                                  </Box>
                              ) : (
                                  <Box sx={{display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5}}>
                                    <PeopleIcon sx={{fontSize: "16px", color: t.t2}}/>
                                    <Typography sx={{fontSize: "13px", color: t.t2}}>כולם</Typography>
                                  </Box>
                              )}
                            </TableCell>

                            {/* Status */}
                            <TableCell sx={{...td, textAlign: "center", display: {xs: "none", md: "table-cell"}}}>
                              <Chip label={n.active ? "פעיל" : "לא פעיל"} size="small" sx={{
                                backgroundColor: n.active ? t.greenLight : t.redLight,
                                color: n.active ? t.greenText : t.redText,
                                fontSize: "11px", height: "24px", fontWeight: 600,
                              }}/>
                            </TableCell>

                            {/* Actions */}
                            <TableCell sx={{...td, textAlign: "center", width: 120}}>
                              <Box sx={{display: "flex", justifyContent: "center", gap: "4px"}} onClick={e => e.stopPropagation()}>
                                <Tooltip title="עריכה">
                                  <IconButton size="small" onClick={openEdit(n)}
                                              sx={{color: t.t2, "&:hover": {color: t.green, bgcolor: `${t.green}18`}}}>
                                    <EditIcon fontSize="small"/>
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title={n.active ? "השבת" : "הפעל"}>
                                  <IconButton size="small" onClick={handleToggleStatus(n)}
                                              sx={{color: t.t2, "&:hover": {color: n.active ? t.amber : t.green, bgcolor: n.active ? `${t.amber}18` : `${t.green}18`}}}>
                                    {n.active ? <PauseIcon fontSize="small"/> : <PlayArrowIcon fontSize="small"/>}
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="מחיקה">
                                  <IconButton size="small" onClick={handleDelete(n)}
                                              sx={{color: t.t2, "&:hover": {color: t.red, bgcolor: `${t.red}18`}}}>
                                    <DeleteIcon fontSize="small"/>
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {pages > 1 && (
                    <Pagination currentPage={f.page} totalPages={pages} totalItems={f.total} itemsPerPage={PAGE_SIZE}
                                onPageChange={p => { const ns = {...f, page: p}; setF(ns); sync(ns); }}/>
                )}
              </>
          )}
        </Box>
      </Box>
  );
};

export default NotificationsPage;
